import React, { useState } from 'react';
import {
  ExternalLink,
  Sparkles,
  Sliders,
  Code2,
  Copy,
  Check,
  Globe,
  Terminal,
  Zap,
  BookOpen,
  ArrowUpRight,
  MousePointerClick,
  FileCode,
  LayoutTemplate
} from 'lucide-react';

const WebsiteTemplatesShowcase = ({ project }) => {
  const [howItWorksTab, setHowItWorksTab] = useState('editor'); // 'editor' | 'config' | 'zerobuild'
  const [configSnippetTab, setConfigSnippetTab] = useState('meta');
  const [copiedGit, setCopiedGit] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  const handleCopyGit = () => {
    navigator.clipboard.writeText('git clone https://github.com/NottKoneko/WebsiteTemplates-.git');
    setCopiedGit(true);
    setTimeout(() => setCopiedGit(false), 2000);
  };

  const configSnippets = {
    meta: `// 1. Site Metadata & Social Preview Card
meta: {
  title: "Your Name — Official Profile",
  description: "Illustrator, streamer, and digital creator.",
  socialImage: "assets/preview.png", // Image for Discord & Twitter embeds
  themeColor: "#FFB3D8",             // Browser accent color
  domainPill: "yourname.com",
  domainUrl: "https://yourname.com"
},`,
    theme: `// 2. Visual Palette & Accent Colors
theme: {
  accentColor: "#FFB3D8",      // Primary pastel highlight
  secondaryColor: "#E8D5FF",   // Lavender hover accents
  backgroundColor: "#FFF8FA",  // Soft gingham tint
  windowBorder: "#2A2035",     // Faux-browser chrome outline
  pattern: "gingham-check",    // Background texture
},`,
    commissions: `// 3. Commission Tiers & Service Rates
commissions: [
  {
    id: "chibi",
    title: "Chibi Character Illustration",
    price: "$45 USD",
    turnaround: "3-5 Business Days",
    includes: ["Transparent PNG render", "300 DPI high-res", "1 Free sketch revision"]
  },
  {
    id: "character-sheet",
    title: "Full Character Reference Sheet",
    price: "$120 USD",
    turnaround: "7-10 Business Days",
    includes: ["Front & back orthographic view", "Color palette swatches", "Props & accessories breakdown"]
  }
],`,
    socials: `// 4. Social Links & Quick Actions
links: [
  { label: "Twitch Live Stream", url: "https://twitch.tv/yourchannel", icon: "twitch", badge: "Live MWF" },
  { label: "Commission Queue & TOS", url: "#commissions", icon: "palette", badge: "Open" },
  { label: "Tip Jar & Support", url: "https://ko-fi.com/yourpage", icon: "coffee" },
  { label: "VOD Archive & YouTube", url: "https://youtube.com/@yourchannel", icon: "youtube" }
]`
  };

  const handleCopySnippet = () => {
    navigator.clipboard.writeText(configSnippets[configSnippetTab]);
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 2000);
  };

  return (
    <div className="space-y-10 animate-fade-in relative">
      {/* Hero Showcase Banner */}
      <div className="bg-[#090714] p-6 sm:p-9 rounded-3xl border border-purple-500/25 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-fuchsia-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-indigo-600/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="relative z-10 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/80 border border-purple-500/40 text-purple-300 text-[11px] font-mono font-bold tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-fuchsia-400" />
              <span>OPEN-SOURCE • ZERO DEPENDENCIES • 100% FREE</span>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono text-gray-400">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>Two Production Templates Available</span>
            </div>
          </div>

          <div className="space-y-3 max-w-3xl">
            <h2 className="text-2xl sm:text-4xl font-serif text-white leading-tight tracking-wide">
              Website &amp; Profile Templates for Creators
            </h2>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
              Drop-in personal landing pages, link hubs, and commission portfolios for VTubers, illustrators, streamers, and developers. No npm dependencies, no build terminal tantrums, and zero hosting fees.
            </p>
          </div>

          {/* Value Prop Comparison Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div className="p-3.5 bg-[#0d0a1c] border border-purple-500/20 rounded-xl flex items-start gap-3">
              <div className="p-2 rounded-lg bg-pink-500/10 text-pink-300 border border-pink-500/30 shrink-0">
                <Zap className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white font-mono">No Build Tools (Vanilla)</h4>
                <p className="text-[11px] text-gray-400 leading-normal mt-0.5">
                  Pure HTML5, CSS3, &amp; ES6. Double-click to open in any browser anywhere.
                </p>
              </div>
            </div>

            <div className="p-3.5 bg-[#0d0a1c] border border-purple-500/20 rounded-xl flex items-start gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10 text-purple-300 border border-purple-500/30 shrink-0">
                <Sliders className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white font-mono">Visual In-Browser Editor</h4>
                <p className="text-[11px] text-gray-400 leading-normal mt-0.5">
                  Click on canvas sections in <code className="text-fuchsia-300">editor.html</code> to customize live &amp; export.
                </p>
              </div>
            </div>

            <div className="p-3.5 bg-[#0d0a1c] border border-purple-500/20 rounded-xl flex items-start gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 shrink-0">
                <Globe className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white font-mono">Free Forever Edge Hosting</h4>
                <p className="text-[11px] text-gray-400 leading-normal mt-0.5">
                  Drag &amp; drop to Cloudflare Pages or GitHub Pages with $0 hosting costs.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Primary Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <a
              href="https://templates.notkoneko.space/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 bg-gradient-to-r from-fuchsia-600 via-purple-600 to-indigo-600 hover:from-fuchsia-500 hover:via-purple-500 hover:to-indigo-500 text-white font-bold rounded-xl text-xs font-mono flex items-center gap-2 shadow-lg shadow-purple-600/30 transition-all hover:scale-[1.02]"
            >
              <ExternalLink className="w-4 h-4" /> Launch Templates Live Hub
            </a>
            <a
              href="https://github.com/NottKoneko/WebsiteTemplates-"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2.5 bg-[#0d0a1c] hover:bg-purple-950/40 text-gray-300 font-semibold rounded-xl text-xs font-mono border border-purple-500/20 flex items-center gap-2 transition-all hover:text-white"
            >
              <Code2 className="w-4 h-4 text-purple-400" /> GitHub Repository
            </a>
            <button
              onClick={handleCopyGit}
              className="px-4 py-2.5 bg-[#0d0a1c] hover:bg-purple-950/40 text-gray-300 font-semibold rounded-xl text-xs font-mono border border-purple-500/20 flex items-center gap-2 transition-all hover:text-white"
            >
              {copiedGit ? (
                <>
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400">Copied clone command!</span>
                </>
              ) : (
                <>
                  <Terminal className="w-4 h-4 text-fuchsia-400" />
                  <span>git clone</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Available Templates Cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-serif text-white flex items-center gap-2.5">
            <LayoutTemplate className="w-5 h-5 text-fuchsia-400" />
            Available Templates
          </h3>
          <span className="text-xs font-mono text-gray-400">2 Production Ready</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Card 1: Retro Pastel Profile */}
          <div className="bg-[#090714] p-5 sm:p-6 rounded-2xl border border-purple-500/20 hover:border-pink-500/40 transition-all flex flex-col justify-between space-y-5 shadow-lg group">
            <div className="space-y-4">
              {/* Preview Image from Readme */}
              <a
                href="https://templates.notkoneko.space/retro-pastel-profile/"
                target="_blank"
                rel="noopener noreferrer"
                className="block relative aspect-[16/10] w-full overflow-hidden rounded-xl border border-pink-500/20 bg-[#120c1f] group/img shadow-inner"
              >
                <img
                  src="/templates/retro-pastel-preview.png"
                  alt="Retro Pastel Profile Template Preview"
                  className="w-full h-full object-cover object-top group-hover/img:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090714]/80 via-transparent to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity flex items-end justify-between p-3">
                  <span className="text-[11px] font-mono text-pink-200 bg-pink-950/80 px-2.5 py-1 rounded-lg border border-pink-500/30 flex items-center gap-1">
                    <span>Live Demo</span>
                    <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </a>

              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono px-2.5 py-1 bg-pink-950/60 text-pink-300 border border-pink-500/30 rounded-md">
                  Faux Browser &amp; Bio Hub
                </span>
                <span className="text-[11px] font-mono text-gray-400">v1.0 Production</span>
              </div>
              <h4 className="text-xl font-bold text-white group-hover:text-pink-300 transition-colors">
                Retro Pastel Profile
              </h4>
              <p className="text-xs text-gray-300 leading-relaxed font-sans">
                A cozy, nostalgic desktop/browser style profile page with soft pastel palettes, clean gingham pattern backgrounds, multi-column widgets, link directory, and a dedicated commission sheet.
              </p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {['3-Column Layout', 'Commissions Page', 'Faux Browser Chrome', 'Web Editor'].map((tag) => (
                  <span key={tag} className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#0d0a1c] text-gray-400 border border-purple-500/20">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-purple-500/20 grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs text-center">
              <a
                href="https://templates.notkoneko.space/retro-pastel-profile/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-pink-500/15 hover:bg-pink-500/25 text-pink-300 border border-pink-500/30 flex items-center justify-center gap-1 transition-all"
              >
                <span>Preview</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <a
                href="https://templates.notkoneko.space/retro-pastel-profile/editor.html"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 text-purple-200 border border-purple-500/30 flex items-center justify-center gap-1 transition-all"
              >
                <Sliders className="w-3.5 h-3.5 text-fuchsia-400" />
                <span>Editor</span>
              </a>
              <a
                href="https://github.com/NottKoneko/WebsiteTemplates-/tree/main/retro-pastel-profile"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-[#0d0a1c] hover:bg-purple-950/40 text-gray-300 border border-purple-500/20 flex items-center justify-center gap-1 transition-all"
              >
                <Code2 className="w-3.5 h-3.5 text-gray-400" />
                <span>Source</span>
              </a>
              <a
                href="https://templates.notkoneko.space/retro-pastel-profile/README.md"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-[#0d0a1c] hover:bg-purple-950/40 text-gray-400 hover:text-white border border-purple-500/20 flex items-center justify-center gap-1 transition-all"
              >
                <BookOpen className="w-3.5 h-3.5 text-pink-400" />
                <span>Guide</span>
              </a>
            </div>
          </div>

          {/* Card 2: Pages Style */}
          <div className="bg-[#090714] p-5 sm:p-6 rounded-2xl border border-purple-500/20 hover:border-indigo-500/40 transition-all flex flex-col justify-between space-y-5 shadow-lg group">
            <div className="space-y-4">
              {/* Preview Image from Readme */}
              <a
                href="https://templates.notkoneko.space/pages-style/"
                target="_blank"
                rel="noopener noreferrer"
                className="block relative aspect-[16/10] w-full overflow-hidden rounded-xl border border-indigo-500/20 bg-[#090a14] group/img shadow-inner"
              >
                <img
                  src="/templates/pages-style-preview.png"
                  alt="Pages Style Template Preview"
                  className="w-full h-full object-cover object-top group-hover/img:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090714]/80 via-transparent to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity flex items-end justify-between p-3">
                  <span className="text-[11px] font-mono text-indigo-200 bg-indigo-950/80 px-2.5 py-1 rounded-lg border border-indigo-500/30 flex items-center gap-1">
                    <span>Live Demo</span>
                    <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </a>

              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono px-2.5 py-1 bg-indigo-950/60 text-indigo-300 border border-indigo-500/30 rounded-md">
                  Editorial Creator Portfolio
                </span>
                <span className="text-[11px] font-mono text-gray-400">v1.0 Production</span>
              </div>
              <h4 className="text-xl font-bold text-white group-hover:text-indigo-300 transition-colors">
                Pages Style
              </h4>
              <p className="text-xs text-gray-300 leading-relaxed font-sans">
                A bold, high-contrast monochrome editorial layout inspired by iconic minimalist Carrd aesthetics with an animated starfield canvas, 16:9 video embed, lore timeline, and a 3-channel client inquiry portal.
              </p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {['Monochrome Editorial', 'Video Hero', '3 Inquiry Channels', 'Web Editor'].map((tag) => (
                  <span key={tag} className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#0d0a1c] text-gray-400 border border-purple-500/20">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-purple-500/20 grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs text-center">
              <a
                href="https://templates.notkoneko.space/pages-style/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/30 flex items-center justify-center gap-1 transition-all"
              >
                <span>Preview</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <a
                href="https://templates.notkoneko.space/pages-style/editor.html"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 text-purple-200 border border-purple-500/30 flex items-center justify-center gap-1 transition-all"
              >
                <Sliders className="w-3.5 h-3.5 text-fuchsia-400" />
                <span>Editor</span>
              </a>
              <a
                href="https://github.com/NottKoneko/WebsiteTemplates-/tree/main/pages-style"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-[#0d0a1c] hover:bg-purple-950/40 text-gray-300 border border-purple-500/20 flex items-center justify-center gap-1 transition-all"
              >
                <Code2 className="w-3.5 h-3.5 text-gray-400" />
                <span>Source</span>
              </a>
              <a
                href="https://templates.notkoneko.space/pages-style/README.md"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-[#0d0a1c] hover:bg-purple-950/40 text-gray-400 hover:text-white border border-purple-500/20 flex items-center justify-center gap-1 transition-all"
              >
                <BookOpen className="w-3.5 h-3.5 text-indigo-400" />
                <span>Guide</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Customization Sandbox */}
      <div className="bg-[#090714] p-6 sm:p-8 rounded-3xl border border-purple-500/25 space-y-6 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-purple-500/20 pb-4">
          <div>
            <span className="text-xs font-mono text-fuchsia-400 uppercase tracking-wider block">
              How Customization Actually Works
            </span>
            <h3 className="text-xl sm:text-2xl font-serif text-white mt-0.5">
              Two Ways to Make It Yours
            </h3>
          </div>

          <div className="flex items-center gap-1 bg-[#0d0a1c] p-1 rounded-xl border border-purple-500/20 font-mono text-xs">
            <button
              onClick={() => setHowItWorksTab('editor')}
              className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                howItWorksTab === 'editor'
                  ? 'bg-purple-600/30 text-fuchsia-300 font-bold border border-purple-500/40'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <MousePointerClick className="w-3.5 h-3.5" />
              <span>In-Browser Visual Editor</span>
            </button>
            <button
              onClick={() => setHowItWorksTab('config')}
              className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                howItWorksTab === 'config'
                  ? 'bg-purple-600/30 text-fuchsia-300 font-bold border border-purple-500/40'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <FileCode className="w-3.5 h-3.5" />
              <span>Direct config.js</span>
            </button>
            <button
              onClick={() => setHowItWorksTab('zerobuild')}
              className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                howItWorksTab === 'zerobuild'
                  ? 'bg-purple-600/30 text-fuchsia-300 font-bold border border-purple-500/40'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
              <span>Zero-Build Philosophy</span>
            </button>
          </div>
        </div>

        {/* TAB 1: VISUAL EDITOR WALKTHROUGH */}
        {howItWorksTab === 'editor' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 font-mono text-xs">
            <div className="p-4 bg-[#0d0a1c] border border-purple-500/20 rounded-2xl space-y-2.5">
              <div className="flex items-center gap-2 text-pink-400 font-bold">
                <span className="w-6 h-6 rounded-lg bg-pink-500/10 border border-pink-500/30 flex items-center justify-center text-xs">
                  1
                </span>
                <span>Open editor.html</span>
              </div>
              <p className="text-gray-300 font-sans leading-relaxed text-xs">
                Double-click <code className="text-pink-300">editor.html</code> in any browser or visit the web customizer link. No server or setup needed.
              </p>
            </div>

            <div className="p-4 bg-[#0d0a1c] border border-purple-500/20 rounded-2xl space-y-2.5">
              <div className="flex items-center gap-2 text-fuchsia-400 font-bold">
                <span className="w-6 h-6 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/30 flex items-center justify-center text-xs">
                  2
                </span>
                <span>Point &amp; Click on Canvas</span>
              </div>
              <p className="text-gray-300 font-sans leading-relaxed text-xs">
                Click directly on any element—avatar, bio, commission tiers, rates, or links—to edit inline and reorder sections with one click.
              </p>
            </div>

            <div className="p-4 bg-[#0d0a1c] border border-purple-500/20 rounded-2xl space-y-2.5">
              <div className="flex items-center gap-2 text-indigo-400 font-bold">
                <span className="w-6 h-6 rounded-lg bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center text-xs">
                  3
                </span>
                <span>One-Click Export</span>
              </div>
              <p className="text-gray-300 font-sans leading-relaxed text-xs">
                Hit <span className="text-indigo-300 font-bold">Export Config</span>, download or copy the generated <code className="text-indigo-300">config.js</code>, and drop it into your folder.
              </p>
            </div>
          </div>
        )}

        {/* TAB 2: INTERACTIVE CONFIG.JS SNIPPET INSPECTOR */}
        {howItWorksTab === 'config' && (
          <div className="space-y-3 font-mono text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {[
                  { id: 'meta', label: '1. Site Meta & Embed' },
                  { id: 'theme', label: '2. Themes & Palette' },
                  { id: 'commissions', label: '3. Commission Tiers' },
                  { id: 'socials', label: '4. Links & Socials' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setConfigSnippetTab(tab.id)}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      configSnippetTab === tab.id
                        ? 'bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/40 font-bold'
                        : 'text-gray-400 hover:text-gray-200 bg-[#0d0a1c]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <button
                onClick={handleCopySnippet}
                className="px-3 py-1.5 rounded-lg bg-purple-950/60 hover:bg-purple-900/60 text-purple-200 border border-purple-500/30 flex items-center gap-1.5 transition-all"
              >
                {copiedSnippet ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-emerald-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy Snippet</span>
                  </>
                )}
              </button>
            </div>

            <div className="p-4 bg-[#05030a] rounded-2xl border border-purple-500/20 overflow-x-auto shadow-inner">
              <pre className="text-purple-200/90 text-xs font-mono leading-relaxed">
                <code>{configSnippets[configSnippetTab]}</code>
              </pre>
            </div>
          </div>
        )}

        {/* TAB 3: ZERO-BUILD PHILOSOPHY */}
        {howItWorksTab === 'zerobuild' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-xs">
            <div className="p-5 bg-rose-950/20 border border-rose-500/30 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-rose-300 font-bold text-sm">
                <span>✕</span>
                <span>The Heavyweight Framework Nightmare</span>
              </div>
              <ul className="space-y-2 text-gray-300 text-xs font-sans">
                <li>• 300MB+ <code className="text-rose-300">node_modules</code> just to display a social link and a bio</li>
                <li>• Webpack, Vite, or Babel config breaks after updating a random package</li>
                <li>• Carrd / Linktree charging $19+/yr just for custom domains or code embeds</li>
                <li>• Complex build scripts fail on CI/CD pipelines</li>
              </ul>
            </div>

            <div className="p-5 bg-emerald-950/20 border border-emerald-500/30 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-emerald-300 font-bold text-sm">
                <span>✓</span>
                <span>The Vanilla Zero-Build Advantage</span>
              </div>
              <ul className="space-y-2 text-gray-300 text-xs font-sans">
                <li>• 0 npm packages, 0 bundlers, 0 compile steps</li>
                <li>• Opens in any web browser immediately—today, or 20 years from now</li>
                <li>• Deploy to GitHub Pages or Cloudflare Pages for 100% free with custom domains</li>
                <li>• Instantaneous page loads with sub-50ms performance</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* 60-Second Quickstart Workflow */}
      <div className="bg-[#090714] p-6 sm:p-8 rounded-3xl border border-purple-500/25 space-y-6 shadow-xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <span className="text-xs font-mono text-fuchsia-400 uppercase tracking-wider block">
              Deployment in 60 Seconds
            </span>
            <h3 className="text-xl sm:text-2xl font-serif text-white">
              From Download to Live URL
            </h3>
          </div>
          <a
            href="https://github.com/NottKoneko/WebsiteTemplates-"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-mono text-fuchsia-400 hover:text-fuchsia-300 flex items-center gap-1 transition-colors hover:underline"
          >
            <span>Read Complete Documentation</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-xs">
          {/* Step 1 */}
          <div className="p-4 bg-[#0d0a1c] border border-purple-500/20 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-fuchsia-400 font-bold block">[ STEP 01 ]</span>
              <span className="text-[10px] text-gray-400">Clone or ZIP</span>
            </div>
            <p className="text-gray-200 font-bold font-sans">Get the Template Files</p>
            <div className="p-2 bg-black/40 rounded border border-purple-500/20 text-[11px] text-gray-300 truncate">
              git clone https://github.com/NottKoneko/WebsiteTemplates-.git
            </div>
            <div className="flex items-center justify-between pt-1">
              <button
                onClick={handleCopyGit}
                className="text-[11px] text-fuchsia-300 hover:text-fuchsia-200 flex items-center gap-1"
              >
                {copiedGit ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedGit ? 'Copied command' : 'Copy command'}</span>
              </button>
              <a
                href="https://github.com/NottKoneko/WebsiteTemplates-/archive/refs/heads/main.zip"
                className="text-[11px] text-gray-400 hover:text-white underline"
              >
                Download ZIP
              </a>
            </div>
          </div>

          {/* Step 2 */}
          <div className="p-4 bg-[#0d0a1c] border border-purple-500/20 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-pink-400 font-bold block">[ STEP 02 ]</span>
              <span className="text-[10px] text-gray-400">Visual or Code</span>
            </div>
            <p className="text-gray-200 font-bold font-sans">Customize Content</p>
            <p className="text-gray-400 text-[11px] font-sans leading-relaxed">
              Open <code className="text-pink-300">editor.html</code> in your browser to tweak everything visually, or edit <code className="text-pink-300">config.js</code> directly.
            </p>
            <div className="text-[10px] text-pink-300 bg-pink-950/40 px-2 py-1 rounded border border-pink-500/20">
              No npm install or terminal required
            </div>
          </div>

          {/* Step 3 */}
          <div className="p-4 bg-[#0d0a1c] border border-purple-500/20 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-purple-300 font-bold block">[ STEP 03 ]</span>
              <span className="text-[10px] text-gray-400">Zero Cost</span>
            </div>
            <p className="text-gray-200 font-bold font-sans">Deploy Anywhere</p>
            <p className="text-gray-400 text-[11px] font-sans leading-relaxed">
              Push to GitHub Pages or drag into Cloudflare Pages. Custom domains and SSL certificates are completely free.
            </p>
            <div className="text-[10px] text-purple-300 bg-purple-950/40 px-2 py-1 rounded border border-purple-500/20">
              100% Free static edge hosting
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteTemplatesShowcase;
